import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { api } from '../api/nocode';
import { computeTrailStartDate } from '../utils/campLogic';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // 1. Check URL for token (simulating handoff from Hub)
        const params = new URLSearchParams(window.location.search);
        let token = params.get('token');
        
        // Also check hash for token if using HashRouter
        if (!token && window.location.hash.includes('?token=')) {
          const hashParts = window.location.hash.split('?');
          if (hashParts.length > 1) {
            const hashParams = new URLSearchParams(hashParts[1]);
            token = hashParams.get('token');
          }
        }

        if (token) {
          localStorage.setItem('gwa_token', token);
          
          // Clean URL safely without breaking HashRouter
          if (window.location.hash.includes('?token=')) {
            const cleanHash = window.location.hash.split('?')[0]; // retains '#/'
            window.history.replaceState(null, '', window.location.pathname + window.location.search + cleanHash);
          } else if (window.location.search.includes('token=')) {
            const newParams = new URLSearchParams(window.location.search);
            newParams.delete('token');
            const newSearch = newParams.toString() ? '?' + newParams.toString() : '';
            window.history.replaceState(null, '', window.location.pathname + newSearch + window.location.hash);
          }
        } else {
          token = localStorage.getItem('gwa_token');
        }

        // 2. Check for Hardwired Master Admin Token
        if (token === 'master_admin_token') {
          let adminId = 'master-admin';
          let adminOwnerId = 'master-admin';
          try {
            const adminCheckRes = await api.get('students_gwaacademy');
            const allUsersAdmin = Array.isArray(adminCheckRes) ? adminCheckRes : adminCheckRes.data || [];
            const adminMatch = allUsersAdmin.find(
              (u) => (u.email || '').trim().toLowerCase() === 'team@gospelwiseauthor.com'
            );
            if (adminMatch?.id) {
              adminId = adminMatch.id;
              adminOwnerId = adminMatch.owner_id || String(adminMatch.id);
            }
          } catch (e) {
            // fallback
          }

          setUser({
            id: adminId,
            name: 'Master Admin',
            email: 'team@gospelwiseauthor.com',
            role: 'admin',
            pass_type: 'allaccess',
            owner_id: adminOwnerId
          });
          setLoading(false);
          return;
        }

        // 3. Process Standard Student JWT
        if (token) {
          let decoded;
          try {
            decoded = jwtDecode(token);
          } catch (e) {
            throw new Error("Invalid JWT token structure");
          }

          // Safely extract email/name from various JWT payload formats
          const searchEmail = decoded.email || decoded.user?.email || decoded.user_metadata?.email;
          const searchName = decoded.name || decoded.user?.name || decoded.user_metadata?.name || 'Student';
          const searchId = decoded.id || decoded.user?.id || crypto.randomUUID();

          if (!searchEmail) {
            throw new Error("No email found in token payload");
          }

          // Check for gwaacademy specific app data in the Hub token
          const appData = decoded.gwaacademy || decoded.apps?.gwaacademy || decoded.app_metadata?.gwaacademy || {};
          const fallbackRole = searchEmail === 'team@gospelwiseauthor.com' ? 'admin' : 'student';
          const tokenRole = appData.role || (decoded.role === 'camper' ? 'student' : decoded.role) || fallbackRole;
          const tokenPassType = appData.pass_type || decoded.pass_type || 'free';

          // Fetch fresh user data from DB.
          let allUsers = [];
          try {
            const res = await api.get('students_gwaacademy');
            allUsers = Array.isArray(res) ? res : res.data || [];
          } catch (fetchErr) {
            console.warn("Could not fetch students from DB, proceeding with token profile:", fetchErr);
          }

          const matchedUser = allUsers.find(
            (u) => (u.email || '').trim().toLowerCase() === searchEmail.trim().toLowerCase()
          );

          if (matchedUser) {
            let resolvedUser = matchedUser;

            // Update role/pass_type if the Hub token has higher privileges than the local DB
            let needsUpdate = false;
            let updatePayload = {};

            if (resolvedUser.first_login_complete !== true) {
              needsUpdate = true;
              updatePayload.first_login_complete = true;
            }
            if (tokenRole === 'admin' && resolvedUser.role !== 'admin') {
              needsUpdate = true;
              updatePayload.role = 'admin';
            } else if (resolvedUser.role === 'camper') {
              needsUpdate = true;
              updatePayload.role = 'student';
            }
            if (tokenPassType === 'allaccess' && resolvedUser.pass_type !== 'allaccess') {
              needsUpdate = true;
              updatePayload.pass_type = 'allaccess';
            }

            // If evergreen user has no trail_start_date, set it to tomorrow
            // so Day 1 unlocks at 7 AM local time tomorrow morning.
            if (['evergreen', 'evergreen-aap'].includes(resolvedUser.pass_type) && !resolvedUser.trail_start_date) {
              needsUpdate = true;
              updatePayload.trail_start_date = computeTrailStartDate();
            }

            if (needsUpdate) {
              try {
                await api.put('students_gwaacademy', resolvedUser.id, updatePayload);
                resolvedUser = { ...resolvedUser, ...updatePayload };
              } catch (updateErr) {
                console.warn("Failed to update user from token sync", updateErr);
              }
            }

            setUser(resolvedUser);
          } else {
            // Auto-provision user if they don't exist yet but have valid JWT
            // Set trail_start_date to tomorrow for evergreen users so Day 1
            // unlocks at 7 AM local time tomorrow morning.
            const trailStart = ['evergreen', 'evergreen-aap'].includes(tokenPassType)
              ? computeTrailStartDate()
              : null;

            const payloadToPost = {
              name: searchName,
              email: searchEmail,
              role: tokenRole === 'admin' ? 'admin' : 'student',
              pass_type: tokenPassType,
              trail_start_date: trailStart,
              first_login_complete: true,
              owner_id: searchId
            };
            
            let newUserData = payloadToPost;
            try {
              const newUserRes = await api.post('students_gwaacademy', payloadToPost);
              if (newUserRes && newUserRes.data && Array.isArray(newUserRes.data) && newUserRes.data.length > 0) {
                newUserData = newUserRes.data[0];
              } else if (newUserRes && newUserRes.data && typeof newUserRes.data === 'object' && newUserRes.data.email) {
                newUserData = newUserRes.data;
              } else if (newUserRes && newUserRes.id) {
                newUserData = { ...payloadToPost, id: newUserRes.id };
              }
            } catch (createErr) {
              console.warn("Auto-provision write failed, using local token profile:", createErr);
            }
            
            setUser(newUserData);
          }
        }
      } catch (err) {
        console.error("Auth error", err);
        localStorage.removeItem('gwa_token');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Hardwired Admin Login function fallback
  const adminLogin = (email, password) => {
    if (email === 'team@gospelwiseauthor.com' && password === 'ZoBoWriMo8954*') {
      const adminUser = {
        id: 1,
        name: 'Master Admin',
        email: 'team@gospelwiseauthor.com',
        role: 'admin',
        pass_type: 'allaccess',
        owner_id: 'master-admin'
      };
      setUser(adminUser);
      localStorage.setItem('gwa_token', 'master_admin_token');
      return true;
    }
    return false;
  };

  const logout = () => {
    localStorage.removeItem('gwa_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, adminLogin, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  const baseUser = Array.isArray(context?.user)
    ? context.user[0]
    : context?.user;

  if (!baseUser || baseUser?.role === 'admin') {
    return context;
  }

  const accessUserId = localStorage.getItem('gwa_run_access_user_id');
  const accessRunId = localStorage.getItem('gwa_run_access_run_id');
  const selectedRunId = localStorage.getItem('gwa_current_run_id');
  const selectedPassType = String(
    localStorage.getItem('gwa_run_pass_type') ?? ''
  ).toLowerCase();
  const hasMatchingRunAccess =
    String(accessUserId ?? '') === String(baseUser.id ?? '') &&
    String(accessRunId ?? '') === String(selectedRunId ?? '');

  let runPassType = String(baseUser?.pass_type ?? 'free').toLowerCase();

  if (
    hasMatchingRunAccess &&
    String(baseUser?.pass_type ?? '').toLowerCase() === 'allaccess' &&
    ['allaccess', 'evergreen-aap'].includes(selectedPassType)
  ) {
    runPassType = 'allaccess';
  }

  return {
    ...context,
    user: {
      ...baseUser,
      pass_type: runPassType
    }
  };
};